import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Plus, Trash2, RefreshCw, Download, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KIT_NUM = ['CASH','BANK','CREDIT RECOVERY','TOTAL AMT','C.P FORM CHARGES',
  'BOOKING KIT','PAID KIT','CREDIT KIT','PENDING KIT',
  'DIARY','PENDRIVE','PEN','DOCUMENT FILE','PRESENTATION FILE'];
const DM_LIST = [
  { key: 'fin_mkt_dm_somnath_jadhav',    label: '1. SOMNATH JADHAV' },
  { key: 'fin_mkt_dm_rashmi_rohankar',   label: '2. RASHMI ROHANKAR' },
  { key: 'fin_mkt_dm_shirish_nisal',     label: '3. SHIRISH NISAL' },
  { key: 'fin_mkt_dm_datta_waditake',    label: '4. DATTA WADITAKE' },
  { key: 'fin_mkt_dm_company',           label: '5. COMPANY' },
  { key: 'fin_mkt_dm_sanjay_gund',       label: '6. SANJAY GUND' },
  { key: 'fin_mkt_dm_sanjay_tulaskar',   label: '7. SANJAY TULASKAR' },
  { key: 'fin_mkt_dm_ramesh_kale',       label: '8. RAMESH KALE' },
  { key: 'fin_mkt_dm_kiran_thite',       label: '9. KIRAN THITE' },
  { key: 'fin_mkt_dm_babasaheb_bade',    label: '10. BABASAHEB BADE' },
  { key: 'fin_mkt_dm_pramod_chavan',     label: '11. PRAMOD CHAVAN' },
  { key: 'fin_mkt_dm_vamik_gadhave',     label: '12. VAMIK GADHAVE' },
  { key: 'fin_mkt_dm_tippanna_chalwadi', label: '13. TIPPANNA CHALWADI' },
  { key: 'fin_mkt_dm_datta_nilak',       label: '14. DATTA NILAK' },
  { key: 'fin_mkt_dm_santosh_bhamnale',  label: '15. SANTOSH BHAMNALE' },
  { key: 'fin_mkt_dm_vishal_supekar',    label: '16. DR. VISHAL SUPEKAR' },
  { key: 'fin_mkt_dm_abbas_absari',      label: '17. ABBAS ABSARI' },
  { key: 'fin_mkt_dm_ganesh_naikare',    label: '18. GANESH NAIKARE' },
];

function fmtNum(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return v || '';
  return n.toFixed(0);
}

function DropBtn(props) {
  const label = props.label;
  const value = props.value;
  const options = props.options;
  const onChange = props.onChange;
  const color = props.color || 'bg-amber-600 text-white';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(function() {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); } }
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={function() { setOpen(function(p) { return !p; }); }}
        className={'flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-colors ' + color}
        data-testid={'drop-' + label.toLowerCase().replace(' ', '-')}
      >
        <span className="text-xs opacity-70 mr-0.5">{label}:</span>
        <span className="truncate max-w-36">{value || 'All'}</span>
        <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 min-w-48 max-h-64 overflow-y-auto">
          <button
            onClick={function() { onChange(''); setOpen(false); }}
            className={'w-full text-left px-4 py-2 text-sm text-slate-500 hover:bg-slate-50 ' + (!value ? 'font-bold' : '')}
          >
            All
          </button>
          {options.map(function(opt, i) {
            return (
              <button key={i}
                onClick={function() { onChange(opt); setOpen(false); }}
                className={'w-full text-left px-4 py-2 text-sm hover:bg-amber-50 transition-colors ' + (value === opt ? 'bg-amber-100 font-bold text-amber-800' : 'text-slate-700')}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FinMktKit() {
  const [selectedDM, setSelectedDM] = useState(DM_LIST[0].key);
  const [dmOpen, setDmOpen] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [newColName, setNewColName] = useState('');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filterADM, setFilterADM] = useState('');
  const [filterCP, setFilterCP] = useState('');
  const inputRef = useRef(null);
  const importRef = useRef(null);
  const dmRef = useRef(null);
  const tableWrapRef = useRef(null);
  const tableElRef = useRef(null);
  const topBarRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);

  const currentDM = DM_LIST.find(function(d) { return d.key === selectedDM; }) || DM_LIST[0];

  const load = useCallback(function() {
    setLoading(true);
    setFilterADM('');
    setFilterCP('');
    Promise.all([
      fetch(API + '/finance/datasets/' + selectedDM + '/columns').then(function(r) { return r.json(); }),
      fetch(API + '/finance/datasets/' + selectedDM + '/rows').then(function(r) { return r.json(); }),
    ]).then(function(results) {
      setColumns(results[0].columns || []);
      setRows(results[1].rows || []);
    }).catch(function() { toast.error('Failed to load data'); })
    .finally(function() { setLoading(false); });
  }, [selectedDM]);

  useEffect(function() { load(); }, [load]);
  useEffect(function() { if (editCell && inputRef.current) { inputRef.current.focus(); } }, [editCell]);

  useEffect(function() {
    function measure() { setScrollW((tableElRef.current && tableElRef.current.offsetWidth) || (tableWrapRef.current && tableWrapRef.current.scrollWidth) || 0); }
    var raf = requestAnimationFrame(function() { requestAnimationFrame(measure); });
    var t1 = setTimeout(measure, 300);
    var t2 = setTimeout(measure, 900);
    window.addEventListener('resize', measure);
    return function() {
      cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener('resize', measure);
    };
  }, [columns, rows, loading]);

  useEffect(function() {
    function handler(e) { if (dmRef.current && !dmRef.current.contains(e.target)) { setDmOpen(false); } }
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, []);

  // Build column name->id map
  const colByName = {};
  columns.forEach(function(c) { colByName[c.name.toUpperCase().trim()] = c.id; });
  const admColId = colByName['ADM NAME'];
  const cpColId = colByName['CHANNEL PARTNER (CP)'];

  // Unique ADM and CP values for dropdowns
  const uniqueADMs = [];
  const seenADM = {};
  rows.forEach(function(r) {
    const v = admColId ? (r.data && r.data[admColId] || '') : '';
    if (v && !seenADM[v]) { seenADM[v] = 1; uniqueADMs.push(v); }
  });

  const uniqueCPs = [];
  const seenCP = {};
  rows.forEach(function(r) {
    const v = cpColId ? (r.data && r.data[cpColId] || '') : '';
    if (v && !seenCP[v]) { seenCP[v] = 1; uniqueCPs.push(v); }
  });

  // Filter rows
  const visibleRows = rows.filter(function(r) {
    if (filterADM && admColId) {
      if ((r.data && r.data[admColId] || '') !== filterADM) { return false; }
    }
    if (filterCP && cpColId) {
      if ((r.data && r.data[cpColId] || '') !== filterCP) { return false; }
    }
    return true;
  });

  // Totals for numeric columns
  const totals = {};
  columns.forEach(function(col) {
    if (KIT_NUM.indexOf(col.name) !== -1) {
      totals[col.id] = visibleRows.reduce(function(s, r) {
        return s + (parseFloat(r.data && r.data[col.id]) || 0);
      }, 0);
    }
  });
  const hasTotals = Object.keys(totals).length > 0;

  const addRow = function() {
    fetch(API + '/finance/datasets/' + selectedDM + '/rows', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(row) { setRows(function(prev) { return prev.concat([row]); }); })
      .catch(function() { toast.error('Failed to add row'); });
  };

  const deleteRow = function(rowId) {
    fetch(API + '/finance/datasets/' + selectedDM + '/rows/' + rowId, { method: 'DELETE' })
      .then(function() { setRows(function(prev) { return prev.filter(function(r) { return r.id !== rowId; }); }); })
      .catch(function() { toast.error('Failed to delete row'); });
  };

  const addColumn = function() {
    if (!newColName.trim()) { return; }
    fetch(API + '/finance/datasets/' + selectedDM + '/columns', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newColName.trim() }),
    }).then(function(r) { return r.json(); })
    .then(function(col) { setColumns(function(prev) { return prev.concat([col]); }); setNewColName(''); })
    .catch(function() { toast.error('Failed to add column'); });
  };

  const startEdit = function(rowId, colId, val) {
    setEditCell({ rowId: rowId, colId: colId });
    setEditVal(val || '');
  };

  const saveEdit = function() {
    if (!editCell) { return; }
    const rowId = editCell.rowId;
    const colId = editCell.colId;
    fetch(API + '/finance/datasets/' + selectedDM + '/rows/' + rowId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: { [colId]: editVal } }),
    }).then(function(r) { return r.json(); })
    .then(function(updated) { setRows(function(prev) { return prev.map(function(r) { return r.id === rowId ? updated : r; }); }); })
    .catch(function() { toast.error('Failed to save'); });
    setEditCell(null);
  };

  const doExport = function() {
    setExporting(true);
    fetch(API + '/finance/datasets/' + selectedDM + '/export')
      .then(function(r) { return r.blob(); })
      .then(function(blob) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = currentDM.label.replace(/[^a-z0-9]/gi, '_') + '.xlsx';
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('Exported!');
      })
      .catch(function() { toast.error('Export failed'); })
      .finally(function() { setExporting(false); });
  };

  const handleImport = function(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) { return; }
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    fetch(API + '/finance/datasets/' + selectedDM + '/import', { method: 'POST', body: fd })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.detail) { throw new Error(data.detail); }
        toast.success('Imported ' + data.rows_imported + ' rows');
        load();
      })
      .catch(function(err) { toast.error(err.message || 'Import failed'); })
      .finally(function() { setImporting(false); if (importRef.current) { importRef.current.value = ''; } });
  };

  return (
    <div className="space-y-4">
      {/* Yellow banner */}
      <div className="rounded-xl overflow-hidden border-2 border-black">
        <div className="py-4 text-center" style={{ background: '#FFD700' }}>
          <p className="text-2xl font-black text-black tracking-widest" style={{ fontFamily: 'serif' }}>MARKETING KIT</p>
          <p className="text-xs font-bold text-black tracking-widest mt-0.5 opacity-70">MARKETING KIT REPORT — DM WISE</p>
        </div>
      </div>

      {/* Filter Row: DM + ADM + CP */}
      <div className="flex items-center gap-3 flex-wrap bg-slate-50 p-3 rounded-xl border border-slate-200">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Select:</p>

        {/* DM Dropdown */}
        <div className="relative" ref={dmRef}>
          <button
            onClick={function() { setDmOpen(function(p) { return !p; }); }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl font-bold text-sm shadow hover:bg-amber-700 transition-colors min-w-52"
            data-testid="dm-dropdown-btn"
          >
            <span className="text-xs opacity-70 mr-0.5">DM:</span>
            <span className="flex-1 text-left truncate">{currentDM.label}</span>
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          </button>
          {dmOpen && (
            <div className="absolute z-50 top-full left-0 mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 w-64 max-h-72 overflow-y-auto">
              {DM_LIST.map(function(dm) {
                return (
                  <button key={dm.key}
                    onClick={function() { setSelectedDM(dm.key); setDmOpen(false); }}
                    className={'w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 transition-colors ' + (selectedDM === dm.key ? 'bg-amber-100 font-bold text-amber-800' : 'text-slate-700')}>
                    {dm.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ADM Dropdown */}
        <DropBtn
          label="ADM"
          value={filterADM}
          options={uniqueADMs}
          onChange={function(v) { setFilterADM(v); setFilterCP(''); }}
          color="bg-blue-600 text-white hover:bg-blue-700"
        />

        {/* CP Dropdown */}
        <DropBtn
          label="CP"
          value={filterCP}
          options={uniqueCPs}
          onChange={function(v) { setFilterCP(v); }}
          color="bg-emerald-600 text-white hover:bg-emerald-700"
        />

        <span className="text-xs text-slate-400 ml-auto">
          {visibleRows.length}/{rows.length} rows
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <Input value={newColName} onChange={function(e) { setNewColName(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter') { addColumn(); } }}
            placeholder="New column name..." className="h-8 w-40 text-sm" />
          <Button size="sm" variant="outline" className="h-8" onClick={addColumn}><Plus className="w-3.5 h-3.5 mr-1" />Col</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={addRow} data-testid="add-row-btn"><Plus className="w-3.5 h-3.5 mr-1" />Row</Button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
          <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} />
          <Button size="sm" variant="outline" className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={function() { if (importRef.current) { importRef.current.click(); } }} disabled={importing}>
            <Upload className="w-3.5 h-3.5 mr-1" />{importing ? 'Importing...' : 'Import Excel'}
          </Button>
          <Button size="sm" className="h-8 text-white bg-amber-600 hover:bg-amber-700" onClick={doExport} disabled={exporting}>
            <Download className="w-3.5 h-3.5 mr-1" />{exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
        <div
          ref={topBarRef}
          className="overflow-x-auto rounded-t-lg"
          style={{ height: 16 }}
          onScroll={function() { if (tableWrapRef.current && topBarRef.current) { tableWrapRef.current.scrollLeft = topBarRef.current.scrollLeft; } }}
        >
          <div style={{ width: scrollW || 1, height: 1 }} />
        </div>
        <div
          ref={tableWrapRef}
          className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm"
          onScroll={function() { if (topBarRef.current && tableWrapRef.current) { topBarRef.current.scrollLeft = tableWrapRef.current.scrollLeft; } }}
        >
          <table ref={tableElRef} className="w-full text-xs border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-amber-900 text-white">
                <th className="px-3 py-2.5 text-center border border-amber-700 w-8">#</th>
                {columns.map(function(col) {
                  return <th key={col.id} className="px-3 py-2.5 text-left font-semibold border border-amber-700 whitespace-nowrap min-w-[90px]">{col.name}</th>;
                })}
                <th className="px-2 py-2.5 border border-amber-700 w-8">Del</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr><td colSpan={columns.length + 2} className="text-center py-8 text-slate-400 italic">No data. Click &quot;+ Row&quot; to add, or use &quot;Import Excel&quot;.</td></tr>
              )}
              {visibleRows.map(function(row, ri) {
                return (
                  <tr key={row.id} className={ri % 2 === 0 ? 'bg-white hover:bg-amber-50' : 'bg-amber-50/40 hover:bg-amber-50'}>
                    <td className="px-3 py-2 text-center text-slate-400 border border-slate-200 font-mono text-[10px]">{ri + 1}</td>
                    {columns.map(function(col) {
                      const val = row.data && row.data[col.id] || '';
                      const isEditing = editCell && editCell.rowId === row.id && editCell.colId === col.id;
                      const isNum = KIT_NUM.indexOf(col.name) !== -1;
                      return (
                        <td key={col.id}
                          className={'px-2 py-1.5 border border-slate-200 cursor-pointer ' + (isNum ? 'text-right font-mono' : '')}
                          onClick={function() { if (!isEditing) { startEdit(row.id, col.id, val); } }}>
                          {isEditing ? (
                            <input ref={inputRef} value={editVal}
                              onChange={function(e) { setEditVal(e.target.value); }}
                              onBlur={saveEdit}
                              onKeyDown={function(e) { if (e.key === 'Enter') { saveEdit(); } if (e.key === 'Escape') { setEditCell(null); } }}
                              className="w-full border border-amber-400 rounded px-1 py-0.5 text-xs outline-none bg-amber-50" />
                          ) : (
                            <span className={val ? 'text-slate-800' : 'text-slate-300 italic'}>{val || '—'}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-center border border-slate-200">
                      <button onClick={function() { deleteRow(row.id); }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {hasTotals && visibleRows.length > 0 && (
              <tfoot>
                <tr className="bg-amber-100 font-bold text-xs border-t-2 border-amber-300">
                  <td className="px-3 py-2 text-center border border-amber-200 text-amber-800">TOT</td>
                  {columns.map(function(col) {
                    return (
                      <td key={col.id} className={'px-2 py-2 border border-amber-200 ' + (totals[col.id] !== undefined ? 'text-right text-amber-900' : '')}>
                        {totals[col.id] !== undefined ? '₹' + totals[col.id].toLocaleString('en-IN') : ''}
                      </td>
                    );
                  })}
                  <td className="border border-amber-200" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </>
      )}
      <p className="text-xs text-slate-400 text-right">{visibleRows.length} row(s) shown · click any cell to edit</p>
    </div>
  );
}

export default FinMktKit;
